/**
 * Combat modes and the shared energy economy.
 *
 * One pool — energy — pays for everything the body does: cannon shots, blade
 * swings, shield absorption, and flight (the jetpack burn that used to be
 * "fuel"). Upgrades buy back efficiency on each of those four uses.
 *
 * This module is the ONLY place those numbers are derived. Damage, cost,
 * absorption and burn multipliers all come out of `combatStats()`, so tuning
 * the game means editing this file and nothing else.
 */

/** The three body-integrated modes. Q steps left, E steps right, no wrap. */
export type CombatMode = "cannon" | "shield" | "blade";

/** Left-to-right order. Shield sits in the middle: one step from either weapon. */
export const MODE_ORDER: readonly CombatMode[] = ["cannon", "shield", "blade"];

/** Where you start, respawn, and land after a flight — the safe, recharging state. */
export const DEFAULT_MODE: CombatMode = "shield";

/** The construct each mode manifests (an ITEMS id — see shared/src/rpg.ts). */
export const MODE_ITEM: Record<CombatMode, string> = {
  cannon: "arm-cannon",
  shield: "energy-shield",
  blade: "light-blade",
};

export const MODE_NAME: Record<CombatMode, string> = {
  cannon: "Arm Cannon",
  shield: "Shield",
  blade: "Light Blade",
};

/** Step one mode left (dir<0) or right (dir>0). The ends CLAMP — cycling past
 *  the blade back to the cannon would make Q/E ambiguous about where you are. */
export function cycleMode(mode: CombatMode, dir: number): CombatMode {
  const i = MODE_ORDER.indexOf(mode);
  const next = Math.max(0, Math.min(MODE_ORDER.length - 1, i + Math.sign(dir)));
  return MODE_ORDER[next];
}

export function isCombatMode(v: unknown): v is CombatMode {
  return typeof v === "string" && (MODE_ORDER as readonly string[]).includes(v);
}

// --- Upgrades -----------------------------------------------------------------
// Four tracks, one per use of energy. Three levels each, bought with credits.

export type UpgradeTrack = "cannon" | "blade" | "shield" | "flight";

export const UPGRADE_TRACKS: readonly UpgradeTrack[] = ["cannon", "blade", "shield", "flight"];

/** Purchased level per track: 0 (stock) … MAX_LEVEL. */
export type UpgradeState = Record<UpgradeTrack, number>;

export const MAX_LEVEL = 3;

/** Credits for the level being bought — index 0 buys level 1. Rising, so the
 *  first point in a new track always beats a third point in an old one. */
export const UPGRADE_COST: readonly number[] = [400, 1100, 2600];

export interface UpgradeTrackDef {
  id: UpgradeTrack;
  name: string;
  blurb: string;
  /** What each level buys, in the player's words. */
  levels: readonly string[];
}

export const UPGRADE_DEFS: Record<UpgradeTrack, UpgradeTrackDef> = {
  cannon: {
    id: "cannon",
    name: "Arm Cannon",
    blurb: "Ranged bolts. Costs energy per shot.",
    levels: [
      "+6 damage · −1.5 energy/shot · +30 rpm",
      "+12 damage · −3 energy/shot · +60 rpm",
      "+18 damage · −4.5 energy/shot · +90 rpm",
    ],
  },
  blade: {
    id: "blade",
    name: "Light Blade",
    blurb: "Close combo strikes. Costs energy per swing.",
    levels: [
      "+8 damage · −2 energy/swing",
      "+16 damage · −4 energy/swing",
      "+24 damage · −6 energy/swing",
    ],
  },
  shield: {
    id: "shield",
    name: "Shield",
    blurb: "Absorbs damage with energy. Recharges faster in shield mode.",
    levels: [
      "−0.3 energy per damage absorbed · +20% shield-mode recharge",
      "−0.6 energy per damage absorbed · +40% shield-mode recharge",
      "−0.9 energy per damage absorbed · +60% shield-mode recharge",
    ],
  },
  flight: {
    id: "flight",
    name: "Flight",
    blurb: "Hover, glide, dive and boost. Burns energy while airborne.",
    levels: [
      "+20 max energy · −15% flight burn",
      "+40 max energy · −30% flight burn",
      "+60 max energy · −45% flight burn",
    ],
  },
};

export function defaultUpgrades(): UpgradeState {
  return { cannon: 0, blade: 0, shield: 0, flight: 0 };
}

/** Cost of the NEXT level in a track, or null when it is maxed. */
export function nextUpgradeCost(upgrades: UpgradeState, track: UpgradeTrack): number | null {
  const level = upgrades[track];
  return level >= MAX_LEVEL ? null : UPGRADE_COST[level];
}

export function validateUpgrades(v: unknown): UpgradeState | null {
  if (!v || typeof v !== "object") return null;
  const raw = v as Record<string, unknown>;
  const out = defaultUpgrades();
  for (const track of UPGRADE_TRACKS) {
    const n = raw[track];
    if (n === undefined) continue;
    if (typeof n !== "number" || !Number.isInteger(n) || n < 0 || n > MAX_LEVEL) return null;
    out[track] = n;
  }
  return out;
}

// --- Derived stats ------------------------------------------------------------

export interface CombatStats {
  /** Energy capacity — the ENERGY bar's 100%. */
  energyMax: number;
  /** Regen while standing on the ground, energy/s. */
  regenGround: number;
  /** Reserved for compatibility; airborne energy no longer recharges. */
  regenAir: number;
  /** Multiplier on regen while in shield mode — the reason to drop back to it. */
  regenShieldMul: number;
  /** Multiplier on every jetpack/boost/glide/dive burn rate. */
  burnMul: number;
  cannonDamage: number;
  cannonCost: number;
  cannonRpm: number;
  bladeDamage: number;
  bladeCost: number;
  /** Energy spent per point of damage the shield absorbs. */
  absorbCost: number;
  /** Energy/s upkeep while a frontal guard is held (RMB in shield mode). */
  guardUpkeep: number;
}

/** Base energy pool, before flight upgrades. Matches the old FUEL_MAX so the
 *  jetpack's feel is unchanged at level 0. */
export const ENERGY_BASE = 100;

/** Damage multipliers the SERVER applies from the victim's state flags. The
 *  client drains energy for exactly the portion these soak away. */
export const SHIELD_SOAK = 0.5; // passive, in shield mode with energy left
export const GUARD_SOAK = 0.25; // RMB frontal guard (the pre-existing block soak)

/**
 * Tuning note — the regen rates are FAST (30/s grounded) because the jetpack was built
 * around them and flight must still feel the way it did. What makes combat cost real is
 * not a slow trickle but the recharge LOCKOUT after a combat spend (CharacterController's
 * combatBlockT) plus the guard zeroing regen outright. Sustained cannon fire therefore
 * drains at its full rate; the pool only comes back once you stop shooting.
 */
export function combatStats(upgrades: UpgradeState): CombatStats {
  const { cannon, blade, shield, flight } = upgrades;
  return {
    energyMax: ENERGY_BASE + 20 * flight,
    regenGround: 30,
    regenAir: 0,
    regenShieldMul: 1.6 + 0.2 * shield,
    burnMul: 1 - 0.15 * flight,
    cannonDamage: 18 + 6 * cannon,
    cannonCost: 9 - 1.5 * cannon,
    cannonRpm: 150 + 30 * cannon,
    bladeDamage: 30 + 8 * blade,
    bladeCost: 12 - 2 * blade,
    absorbCost: 1.6 - 0.3 * shield,
    // The guard's real cost is the recharge it forbids; the upkeep is the slow bleed on top,
    // so a guard held forever eventually empties even if nothing hits you.
    guardUpkeep: 6,
  };
}
